/**--external-- */
import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";

/**--internal-- */
import RichTextEditor from "UIComponents/RichTextEditor";
import Textview from "UIComponents/Textview";
import ACLStore from "lib/aclStore";
import DocEditor from "UIComponents/DocEditor";
import {
  DOC_EDITOR_STYLES,
  DOC_EDITOR_OVERRIDE_ELEMENTS,
} from "Worksheet/modules/WorksheetConstants";

/**--relative-- */
import Field from "../Field";
import { getRichTextValue } from "../FieldUtils";
import classes from "./FieldRichText.scss";

class FieldRichText extends Field {
  renderPreviewContent = () => {
    const { showLexicalRTE = true, readOnly } = this.props;

    return (
      <div className={classes.previewContainer}>
        <RichTextEditor
          mode="edit"
          width={"100%"}
          height={170}
          showLexicalRTE={showLexicalRTE}
          readOnly={true}
        />
      </div>
    );
  };

  renderEdit = () => {
    const {
      updateInputField,
      value,
      name,
      onFocusInputField,
      onBlurInputField,
      customRef,
      lockFieldForSchoolGroups,
      readOnly,
      isRequired,
      isMediaRestricted,
      renderNewEditor,
      customValidation,
      isAIEnabledInRTE,
      enableEditorObserver,
      shouldCallOnBlur,
      showLexicalRTE = true,
      hideErrorMessage,
      isActive,
      toolbarPosition,
      isHeightAdaptive,
      pluginsConfig: parentPluginsConfig,
      isToolbarSticky,
      height,
      showDragHandles = true,
      showDocEditor,
      extendedToolbarButtons,
      allowCopyPasteInEquationEditor = true,
      disabledCopyButtonTooltipText = null,
      isLinkDisabled,
    } = this.props;

    if (showDocEditor) {
      const config = {
        floatingToolbar: {
          AI: {
            isEnabled: isAIEnabledInRTE,
          },
        },
      };

      return (
        <DocEditor
          value={value}
          name={name}
          updateInputField={(value, isEmpty) => {
            updateInputField(isEmpty ? { [name]: "" } : value);
          }}
          onFocusInputField={onFocusInputField}
          onBlurInputField={onBlurInputField}
          readOnly={readOnly}
          viewOnly={false}
          config={config}
          nodesDisabled={{
            embed: isMediaRestricted,
            image: isMediaRestricted,
            link: _.isBoolean(isLinkDisabled)
              ? isLinkDisabled
              : isMediaRestricted,
          }}
          showDragHandles={true}
          isCompact={true}
          compactVariantMinHeight={
            isHeightAdaptive ? "fit-content" : height || 216
          }
          containerStyle={DOC_EDITOR_STYLES.CONTAINER_STYLES}
          compactVariantStyles={DOC_EDITOR_STYLES.COMPACT_VARIANT_STYLES}
          isDropdownBodyLevel={true}
          isTableSizeContained={true}
          tableMaxWidth={650}
          tableDefaultWidth={300}
          customRef={customRef}
          lockFieldForSchoolGroups={lockFieldForSchoolGroups}
          isRequired={isRequired}
          renderNewEditor={renderNewEditor}
          isMediaRestricted={isMediaRestricted}
          customValidation={customValidation}
          enableEditorObserver={enableEditorObserver}
          shouldCallOnBlur={shouldCallOnBlur}
          showLexicalRTE={showLexicalRTE}
          hideErrorMessage={hideErrorMessage}
          isActive={isActive}
          toolbarPosition={toolbarPosition}
          isHeightAdaptive={isHeightAdaptive}
          isToolbarSticky={isToolbarSticky}
          extendedToolbarButtons={extendedToolbarButtons}
          isMathsEquationEnabled={true}
          saveDelay={300}
          allowCopyPasteInEquationEditor={allowCopyPasteInEquationEditor}
          disabledCopyButtonTooltipText={disabledCopyButtonTooltipText}
          overrideElements={DOC_EDITOR_OVERRIDE_ELEMENTS}
        />
      );
    }

    const pluginsConfig = {
      ai: {
        isEnabled: isAIEnabledInRTE,
      },
      image: {
        isEnabled: !isMediaRestricted,
        allowPaste: !isMediaRestricted,
      },
      file: {
        isEnabled: !isMediaRestricted,
      },
      video: {
        isEnabled:
          !isMediaRestricted && ACLStore.can("FeatureFlag:EnableRTEVideo"),
      },
      audio: {
        isEnabled: !isMediaRestricted,
      },
      link: {
        isEnabled: _.isBoolean(isLinkDisabled)
          ? !isLinkDisabled
          : !isMediaRestricted,
      },
      toolbar: {
        uploadButton: { isEnabled: !isMediaRestricted },
        ...parentPluginsConfig?.toolbar,
      },
    };

    return (
      <RichTextEditor
        mode="edit"
        width={"100%"}
        height={height}
        updateInputField={updateInputField}
        onFocusInputField={onFocusInputField}
        onBlurInputField={onBlurInputField}
        value={value}
        name={name}
        customRef={customRef}
        lockFieldForSchoolGroups={lockFieldForSchoolGroups}
        readOnly={readOnly}
        isRequired={isRequired}
        renderNewEditor={renderNewEditor}
        pluginsConfig={pluginsConfig}
        isMediaRestricted={isMediaRestricted}
        customValidation={customValidation}
        enableEditorObserver={enableEditorObserver}
        shouldCallOnBlur={shouldCallOnBlur}
        showLexicalRTE={showLexicalRTE}
        hideErrorMessage={hideErrorMessage}
        isActive={isActive}
        toolbarPosition={toolbarPosition}
        isHeightAdaptive={isHeightAdaptive}
        isToolbarSticky={isToolbarSticky}
        showDragHandles={showDragHandles}
        allowCopyPasteInEquationEditor={allowCopyPasteInEquationEditor}
        disabledCopyButtonTooltipText={disabledCopyButtonTooltipText}
      />
    );
  };
  renderView = () => {
    const {
      customRef,
      value,
      fieldKey,
      emptyText,
      textStyle,
      applyDefaultClass,
      customTextStyle,
      isRtlAlignmentRequired,
      sanitizationType,
      showMediaLinkInTheatreMode,
      shouldInheritFontSize,
      dsVersion,
      showDocEditor,
    } = this.props;

    return (
      <Textview
        customRef={customRef}
        name={fieldKey}
        value={getRichTextValue({ value })}
        mode="view"
        emptyText={emptyText}
        textStyle={textStyle}
        applyDefaultClass={applyDefaultClass}
        customTextStyle={customTextStyle}
        isRtlAlignmentRequired={isRtlAlignmentRequired}
        sanitizationType={sanitizationType}
        showMediaLinkInTheatreMode={showMediaLinkInTheatreMode}
        shouldInheritFontSize={shouldInheritFontSize}
        dsVersion={dsVersion}
        showDocEditor={showDocEditor}
      />
    );
  };
}

export default FieldRichText;
FieldRichText.displayName = "FieldRichText";

FieldRichText.propTypes = {
  updateInputField: PropTypes.func,
  fieldValue: PropTypes.string,
  name: PropTypes.string,
  onFocusInputField: PropTypes.func,
  onBlurInputField: PropTypes.func,
  customRef: PropTypes.func,
  fieldLockedObject: PropTypes.object,
  renderNewEditor: PropTypes.bool,
  isMediaRestricted: PropTypes.bool,
  customValidation: PropTypes.func,
  shouldCallOnBlur: PropTypes.bool,
  hideErrorMessage: PropTypes.bool,
  isActive: PropTypes.bool,
  showDocEditor: PropTypes.bool,
  isLinkDisabled: PropTypes.bool,
};

FieldRichText.defaultProps = {
  ...Field.defaultProps,
  renderNewEditor: true,
  isMediaRestricted: false,
  shouldCallOnBlur: true,
  hideErrorMessage: false,
  isActive: false,
  height: 170,
  showDocEditor: false,
  isLinkDisabled: null,
};
